import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  scoreSortRound,
  scoreLogDistanceGuess,
  haversineDistanceKm,
  formatDistanceKm,
  formatLatitude,
  type Difficulty,
} from "@hunch/core";
import { GAME_BRANDING } from "@hunch/branding";
import { useI18n } from "../../i18n/index.js";
import { SortEngine } from "../../components/game/SortEngine.js";
import { DuelEngine } from "../../components/game/DuelEngine.js";
import { SliderEstimateEngine } from "../../components/game/SliderEstimateEngine.js";
import { CrowflightMap, type MapPoint } from "../../components/game/CrowflightMap.js";
import { CountdownBar } from "../../components/game/CountdownBar.js";
import { PointsBurst } from "../../components/game/PointsBurst.js";
import { RoundIntroOverlay } from "../../components/game/RoundIntroOverlay.js";
import { HintButton } from "../../components/game/HintButton.js";
import { HintMapOverlay } from "../../components/game/HintMapOverlay.js";
import type { RoundSummary } from "../../components/game/EndScreen.js";
import { ROUND_TIME_SECONDS } from "../../lib/roundTime.js";
import { SortRevealList, type SortRevealEntry } from "./SortRevealList.js";
import type { CrowflightNewGame, CrowflightPlace } from "./api.js";
import styles from "./CrowflightPlay.module.css";

type Step = "r1" | "r2" | "r3" | "r4";

interface CrowflightPlayProps {
  game: CrowflightNewGame;
  difficulty: Difficulty;
  onFinish: (rounds: RoundSummary[], totalScore: number) => void;
}

const TIMEOUT_ADVANCE_DELAY_MS = 3000;
/** Flat per-use cost of the "give hint" map reveal, see docs/decisions.md. */
const HINT_COST = 20;

function placeName(p: CrowflightPlace, lang: "de" | "en"): string {
  return lang === "de" ? p.nameDe : p.nameEn;
}

function toMapPoint(p: CrowflightPlace, lang: "de" | "en"): MapPoint {
  return { id: p.id, lat: p.lat, lon: p.lon, name: placeName(p, lang) };
}

const MIN_KM = 10;
const MAX_KM = 20000;

function kmToRawPct(km: number): number {
  const t = Math.log(km / MIN_KM) / Math.log(MAX_KM / MIN_KM);
  return Math.max(0, Math.min(100, t * 100));
}

export function CrowflightPlay({ game, difficulty, onFinish }: CrowflightPlayProps) {
  const { t } = useI18n();
  const [step, setStep] = useState<Step>("r1");
  const [collected, setCollected] = useState<RoundSummary[]>([]);
  // The currently active round's own provisional points — reported up by
  // each round component as they become known (e.g. once revealed/locked,
  // or duel-by-duel for R3), so the total stays live within a round instead
  // of only updating once the round is fully complete.
  const [currentRoundPoints, setCurrentRoundPoints] = useState(0);

  function handleRoundComplete(summary: RoundSummary) {
    const next = [...collected, summary];
    if (step === "r4") {
      onFinish(
        next,
        next.reduce((sum, r) => sum + r.points, 0),
      );
      return;
    }
    setCollected(next);
    setCurrentRoundPoints(0);
    setStep(step === "r1" ? "r2" : step === "r2" ? "r3" : "r4");
  }

  const roundNumber = step === "r1" ? 1 : step === "r2" ? 2 : step === "r3" ? 3 : 4;
  const runningTotal = collected.reduce((sum, r) => sum + r.points, 0) + currentRoundPoints;

  return (
    <main className={styles.main}>
      <div className={styles.statusRow}>
        <span className={styles.progress}>
          {GAME_BRANDING.crowflight.name} · {t("timeslip.play.roundOf", { current: roundNumber, total: 4 })}
        </span>
        <span className={styles.totalScore}>{t("timeslip.play.totalScore", { total: runningTotal })}</span>
      </div>
      {step === "r1" && (
        <R1Round
          game={game}
          difficulty={difficulty}
          roundNumber={roundNumber}
          onComplete={handleRoundComplete}
          onPointsChange={setCurrentRoundPoints}
        />
      )}
      {step === "r2" && (
        <R2Round
          game={game}
          difficulty={difficulty}
          roundNumber={roundNumber}
          onComplete={handleRoundComplete}
          onPointsChange={setCurrentRoundPoints}
        />
      )}
      {step === "r3" && (
        <R3Round
          game={game}
          difficulty={difficulty}
          roundNumber={roundNumber}
          onComplete={handleRoundComplete}
          onPointsChange={setCurrentRoundPoints}
        />
      )}
      {step === "r4" && (
        <R4Round
          game={game}
          difficulty={difficulty}
          roundNumber={roundNumber}
          onComplete={handleRoundComplete}
          onPointsChange={setCurrentRoundPoints}
        />
      )}
      <Link to="/" className={styles.cancelLink}>
        {t("timeslip.play.cancelGame")}
      </Link>
    </main>
  );
}

function ranksByValue(places: CrowflightPlace[], valueOf: (p: CrowflightPlace) => number): Map<number, number> {
  const sorted = places.slice().sort((a, b) => valueOf(a) - valueOf(b));
  const ranks = new Map<number, number>();
  sorted.forEach((p, i) => ranks.set(p.id, i + 1));
  return ranks;
}

function R1Round({
  game,
  difficulty,
  roundNumber,
  onComplete,
  onPointsChange,
}: {
  game: CrowflightNewGame;
  difficulty: Difficulty;
  roundNumber: number;
  onComplete: (s: RoundSummary) => void;
  onPointsChange: (points: number) => void;
}) {
  const { lang, t } = useI18n();
  const reference = game.r1.reference;
  const [order, setOrder] = useState<CrowflightPlace[]>(game.r1.places);
  const [checked, setChecked] = useState(false);
  const roundLimit = ROUND_TIME_SECONDS[difficulty];
  const [timeLeft, setTimeLeft] = useState<number | null>(roundLimit);
  const [timedOut, setTimedOut] = useState(false);
  const [introDone, setIntroDone] = useState(false);
  const [hintedIds, setHintedIds] = useState<Set<number>>(new Set());
  const [hintPlace, setHintPlace] = useState<CrowflightPlace | null>(null);

  function handleHint(p: CrowflightPlace) {
    setHintedIds((prev) => (prev.has(p.id) ? prev : new Set(prev).add(p.id)));
    setHintPlace(p);
  }
  const hintPenalty = hintedIds.size * HINT_COST;

  const distanceOf = (p: CrowflightPlace) => haversineDistanceKm(reference, p);
  const ranks = useMemo(() => ranksByValue(game.r1.places, distanceOf), [game.r1.places]);

  const result = useMemo(() => {
    if (!checked) return null;
    return scoreSortRound(order.map((p) => ranks.get(p.id)!), game.pointsPerItem);
  }, [checked, order, ranks, game.pointsPerItem]);
  const finalPoints = result ? Math.max(0, result.totalPoints - hintPenalty) : 0;

  useEffect(() => {
    onPointsChange(finalPoints);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finalPoints]);

  useEffect(() => {
    if (roundLimit === null || checked || !introDone) return;
    const id = window.setInterval(() => {
      setTimeLeft((prev) => {
        if (prev === null) return null;
        if (prev <= 1) {
          setTimedOut(true);
          setChecked(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => window.clearInterval(id);
  }, [checked, roundLimit, introDone]);

  useEffect(() => {
    if (!timedOut || !checked || !result) return;
    const id = window.setTimeout(() => {
      onComplete({
        points: finalPoints,
        maxPoints: game.pointsPerItem * order.length,
        greenSquares: result.greenIndices.length,
        totalSquares: order.length,
      });
    }, TIMEOUT_ADVANCE_DELAY_MS);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timedOut, checked, result]);

  const entries: SortRevealEntry[] = useMemo(() => {
    if (!result) return [];
    const greenSet = new Set(result.greenIndices);
    return order.map((p, i) => ({
      place: p,
      isGreen: greenSet.has(i + 1),
      truePosition: ranks.get(p.id)!,
      points: game.pointsPerItem,
      valueLabel: `${formatDistanceKm(distanceOf(p), lang)} km`,
    }));
  }, [result, order, ranks, lang, game.pointsPerItem]);

  const correctOrder = useMemo(
    () => game.r1.places.slice().sort((a, b) => distanceOf(a) - distanceOf(b)),
    [game.r1.places],
  );

  const distancesKm = useMemo(() => {
    const map: Record<number, number> = {};
    for (const p of order) map[p.id] = distanceOf(p);
    return map;
  }, [order]);

  return (
    <div>
      {!introDone && (
        <RoundIntroOverlay
          eyebrow={t("timeslip.play.roundOf", { current: roundNumber, total: 4 })}
          title={t("crowflight.play.r1.title")}
          onDone={() => setIntroDone(true)}
        />
      )}
      <h2 className={styles.roundTitle}>{t("crowflight.play.r1.title")}</h2>
      <p className={styles.referenceLabel}>
        <span className={styles.placeName}>{placeName(reference, lang)}</span>
        {!checked && (
          <HintButton
            revealed={hintedIds.has(reference.id)}
            onClick={() => handleHint(reference)}
            getHintLabel={t("crowflight.play.hint.get")}
            viewHintLabel={t("crowflight.play.hint.view")}
          />
        )}
      </p>

      {timeLeft !== null && roundLimit !== null && (
        <CountdownBar secondsLeft={timeLeft} secondsTotal={roundLimit} />
      )}
      {timedOut && <div className={styles.timeUpBanner}>{t("timeslip.play.timeUp")}</div>}

      {!checked && (
        <SortEngine
          items={order}
          getId={(p) => String(p.id)}
          renderLabel={(p) => (
            <span className={styles.sortLabel}>
              <span className={styles.sortLabelText}>{placeName(p, lang)}</span>
              <HintButton
                revealed={hintedIds.has(p.id)}
                onClick={() => handleHint(p)}
                getHintLabel={t("crowflight.play.hint.get")}
                viewHintLabel={t("crowflight.play.hint.view")}
              />
            </span>
          )}
          onChange={setOrder}
          topLabel={t("crowflight.play.r1.near")}
          bottomLabel={t("crowflight.play.r1.far")}
          dragHandleLabel={t("timeslip.play.dragHandle")}
        />
      )}
      {hintPlace && (
        <HintMapOverlay
          place={toMapPoint(hintPlace, lang)}
          lang={lang}
          onClose={() => setHintPlace(null)}
          closeLabel={t("crowflight.play.hint.close")}
        />
      )}
      {hintedIds.size > 0 && <PointsBurst points={-HINT_COST} triggerKey={`hint-${hintedIds.size}`} />}

      {checked && result && (
        <>
          <PointsBurst points={finalPoints} triggerKey="r1" />
          <p className={styles.summary}>
            {t("timeslip.play.summaryLine", {
              correct: result.greenIndices.length,
              total: order.length,
              points: finalPoints,
            })}
          </p>
          <SortRevealList
            entries={entries}
            correctOrder={correctOrder}
            showCorrectOrderLabel={t("timeslip.play.showCorrectOrder")}
          />
          <CrowflightMap
            reference={{ id: reference.id, lat: reference.lat, lon: reference.lon, name: placeName(reference, lang) }}
            points={order.map((p) => ({ id: p.id, lat: p.lat, lon: p.lon, name: placeName(p, lang) }))}
            mode="arcs"
            distancesKm={distancesKm}
            lang={lang}
          />
        </>
      )}

      <div className={styles.actions}>
        {!checked ? (
          <button type="button" className={styles.primaryButton} onClick={() => setChecked(true)}>
            {t("timeslip.play.check")}
          </button>
        ) : (
          <button
            type="button"
            className={styles.primaryButton}
            onClick={() =>
              result &&
              onComplete({
                points: finalPoints,
                maxPoints: game.pointsPerItem * order.length,
                greenSquares: result.greenIndices.length,
                totalSquares: order.length,
              })
            }
          >
            {t("timeslip.play.next")}
          </button>
        )}
      </div>
    </div>
  );
}

function R2Round({
  game,
  difficulty,
  roundNumber,
  onComplete,
  onPointsChange,
}: {
  game: CrowflightNewGame;
  difficulty: Difficulty;
  roundNumber: number;
  onComplete: (s: RoundSummary) => void;
  onPointsChange: (points: number) => void;
}) {
  const { lang, t } = useI18n();
  const [order, setOrder] = useState<CrowflightPlace[]>(game.r2.places);
  const [checked, setChecked] = useState(false);
  const roundLimit = ROUND_TIME_SECONDS[difficulty];
  const [timeLeft, setTimeLeft] = useState<number | null>(roundLimit);
  const [timedOut, setTimedOut] = useState(false);
  const [introDone, setIntroDone] = useState(false);
  const [hintedIds, setHintedIds] = useState<Set<number>>(new Set());
  const [hintPlace, setHintPlace] = useState<CrowflightPlace | null>(null);

  function handleHint(p: CrowflightPlace) {
    setHintedIds((prev) => (prev.has(p.id) ? prev : new Set(prev).add(p.id)));
    setHintPlace(p);
  }
  const hintPenalty = hintedIds.size * HINT_COST;

  // North (highest latitude) ranks first — rank by *descending* latitude.
  const latRankValue = (p: CrowflightPlace) => -p.lat;
  const ranks = useMemo(() => ranksByValue(game.r2.places, latRankValue), [game.r2.places]);

  const result = useMemo(() => {
    if (!checked) return null;
    return scoreSortRound(order.map((p) => ranks.get(p.id)!), game.pointsPerItem);
  }, [checked, order, ranks, game.pointsPerItem]);
  const finalPoints = result ? Math.max(0, result.totalPoints - hintPenalty) : 0;

  useEffect(() => {
    onPointsChange(finalPoints);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finalPoints]);

  useEffect(() => {
    if (roundLimit === null || checked || !introDone) return;
    const id = window.setInterval(() => {
      setTimeLeft((prev) => {
        if (prev === null) return null;
        if (prev <= 1) {
          setTimedOut(true);
          setChecked(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => window.clearInterval(id);
  }, [checked, roundLimit, introDone]);

  useEffect(() => {
    if (!timedOut || !checked || !result) return;
    const id = window.setTimeout(() => {
      onComplete({
        points: finalPoints,
        maxPoints: game.pointsPerItem * order.length,
        greenSquares: result.greenIndices.length,
        totalSquares: order.length,
      });
    }, TIMEOUT_ADVANCE_DELAY_MS);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timedOut, checked, result]);

  const entries: SortRevealEntry[] = useMemo(() => {
    if (!result) return [];
    const greenSet = new Set(result.greenIndices);
    return order.map((p, i) => ({
      place: p,
      isGreen: greenSet.has(i + 1),
      truePosition: ranks.get(p.id)!,
      points: game.pointsPerItem,
      valueLabel: formatLatitude(p.lat, lang),
    }));
  }, [result, order, ranks, lang, game.pointsPerItem]);

  const correctOrder = useMemo(
    () => game.r2.places.slice().sort((a, b) => b.lat - a.lat),
    [game.r2.places],
  );

  return (
    <div>
      {!introDone && (
        <RoundIntroOverlay
          eyebrow={t("timeslip.play.roundOf", { current: roundNumber, total: 4 })}
          title={t("crowflight.play.r2.title")}
          onDone={() => setIntroDone(true)}
        />
      )}
      <h2 className={styles.roundTitle}>{t("crowflight.play.r2.title")}</h2>
      <p className={styles.referenceLabel}>{t("crowflight.play.r2.instruction")}</p>

      {timeLeft !== null && roundLimit !== null && (
        <CountdownBar secondsLeft={timeLeft} secondsTotal={roundLimit} />
      )}
      {timedOut && <div className={styles.timeUpBanner}>{t("timeslip.play.timeUp")}</div>}

      {!checked && (
        <SortEngine
          items={order}
          getId={(p) => String(p.id)}
          renderLabel={(p) => (
            <span className={styles.sortLabel}>
              <span className={styles.sortLabelText}>{placeName(p, lang)}</span>
              <HintButton
                revealed={hintedIds.has(p.id)}
                onClick={() => handleHint(p)}
                getHintLabel={t("crowflight.play.hint.get")}
                viewHintLabel={t("crowflight.play.hint.view")}
              />
            </span>
          )}
          onChange={setOrder}
          topLabel={t("crowflight.play.r2.north")}
          bottomLabel={t("crowflight.play.r2.south")}
          dragHandleLabel={t("timeslip.play.dragHandle")}
        />
      )}
      {hintPlace && (
        <HintMapOverlay
          place={toMapPoint(hintPlace, lang)}
          lang={lang}
          onClose={() => setHintPlace(null)}
          closeLabel={t("crowflight.play.hint.close")}
        />
      )}
      {hintedIds.size > 0 && <PointsBurst points={-HINT_COST} triggerKey={`hint-${hintedIds.size}`} />}

      {checked && result && (
        <>
          <PointsBurst points={finalPoints} triggerKey="r2" />
          <p className={styles.summary}>
            {t("timeslip.play.summaryLine", {
              correct: result.greenIndices.length,
              total: order.length,
              points: finalPoints,
            })}
          </p>
          <SortRevealList
            entries={entries}
            correctOrder={correctOrder}
            showCorrectOrderLabel={t("timeslip.play.showCorrectOrder")}
          />
          <CrowflightMap
            points={order.map((p) => ({ id: p.id, lat: p.lat, lon: p.lon, name: placeName(p, lang) }))}
            mode="latitude"
            lang={lang}
          />
        </>
      )}

      <div className={styles.actions}>
        {!checked ? (
          <button type="button" className={styles.primaryButton} onClick={() => setChecked(true)}>
            {t("timeslip.play.check")}
          </button>
        ) : (
          <button
            type="button"
            className={styles.primaryButton}
            onClick={() =>
              result &&
              onComplete({
                points: finalPoints,
                maxPoints: game.pointsPerItem * order.length,
                greenSquares: result.greenIndices.length,
                totalSquares: order.length,
              })
            }
          >
            {t("timeslip.play.next")}
          </button>
        )}
      </div>
    </div>
  );
}

function R3Round({
  game,
  difficulty,
  roundNumber,
  onComplete,
  onPointsChange,
}: {
  game: CrowflightNewGame;
  difficulty: Difficulty;
  roundNumber: number;
  onComplete: (s: RoundSummary) => void;
  onPointsChange: (points: number) => void;
}) {
  const { lang, t } = useI18n();
  const duels = game.r3.duels;
  const [duelIndex, setDuelIndex] = useState(0);
  const [feedback, setFeedback] = useState<{ correct: boolean; text: string; chosenId: number } | null>(null);
  const [results, setResults] = useState<boolean[]>([]);
  const [finished, setFinished] = useState(false);
  // The intro overlay (announcing "Near or far?") plays once for the whole
  // round, not per duel — introDone doesn't reset when duelIndex changes.
  const [introDone, setIntroDone] = useState(false);
  // Per-duel countdown (same mechanic as the other rounds, see
  // docs/decisions.md), restarting with each new duel rather than spanning
  // the whole round — a duel is the natural "answer this now" unit here.
  const roundLimit = ROUND_TIME_SECONDS[difficulty];
  const [timeLeft, setTimeLeft] = useState<number | null>(roundLimit);
  const [timedOut, setTimedOut] = useState(false);
  // One set for the whole round (not reset per duel) — each duel's places
  // are freshly generated, so ids never collide across duels anyway.
  const [hintedIds, setHintedIds] = useState<Set<number>>(new Set());
  const [hintPlace, setHintPlace] = useState<CrowflightPlace | null>(null);

  function handleHint(p: CrowflightPlace) {
    setHintedIds((prev) => (prev.has(p.id) ? prev : new Set(prev).add(p.id)));
    setHintPlace(p);
  }
  const hintPenalty = hintedIds.size * HINT_COST;
  const livePoints = Math.max(0, results.filter(Boolean).length * game.pointsPerItem - hintPenalty);

  useEffect(() => {
    onPointsChange(livePoints);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [livePoints]);

  const current = duels[duelIndex];

  function handleChoose(chosenId: number) {
    if (!current || feedback) return;
    const [a, b] = current.options;
    const da = haversineDistanceKm(current.reference, a);
    const db = haversineDistanceKm(current.reference, b);
    const nearer = da <= db ? a : b;
    const [first, second, d1, d2] = da <= db ? [a, b, da, db] : [b, a, db, da];
    const correct = chosenId === nearer.id;

    setFeedback({
      correct,
      chosenId,
      text: t("crowflight.play.r3.feedback", {
        a: placeName(first, lang),
        distA: formatDistanceKm(d1, lang),
        b: placeName(second, lang),
        distB: formatDistanceKm(d2, lang),
      }),
    });
  }

  useEffect(() => {
    if (roundLimit === null || feedback || !current || !introDone) return;
    const id = window.setInterval(() => {
      setTimeLeft((prev) => {
        if (prev === null) return null;
        if (prev <= 1) {
          // No pick made in time: counts as wrong, chosenId -1 never matches
          // either option so DuelEngine highlights neither as "selected".
          const [a, b] = current.options;
          const da = haversineDistanceKm(current.reference, a);
          const db = haversineDistanceKm(current.reference, b);
          const [first, second, d1, d2] = da <= db ? [a, b, da, db] : [b, a, db, da];
          setTimedOut(true);
          setFeedback({
            correct: false,
            chosenId: -1,
            text: t("crowflight.play.r3.feedback", {
              a: placeName(first, lang),
              distA: formatDistanceKm(d1, lang),
              b: placeName(second, lang),
              distB: formatDistanceKm(d2, lang),
            }),
          });
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => window.clearInterval(id);
  }, [feedback, current, lang, t, roundLimit, introDone]);

  function handleNext() {
    const nextResults = [...results, feedback?.correct ?? false];
    setResults(nextResults);
    setFeedback(null);
    setTimedOut(false);
    setTimeLeft(roundLimit);
    if (duelIndex + 1 >= duels.length) {
      setFinished(true);
    } else {
      setDuelIndex(duelIndex + 1);
    }
  }

  // Timed-out duels auto-advance after a short pause — a manual "Weiter"
  // click in the meantime advances immediately and cancels this.
  useEffect(() => {
    if (!timedOut || !feedback) return;
    const id = window.setTimeout(handleNext, TIMEOUT_ADVANCE_DELAY_MS);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timedOut, feedback, duelIndex]);

  const correctCount = results.filter(Boolean).length;
  const points = livePoints;

  if (finished) {
    return (
      <div>
        <h2 className={styles.roundTitle}>{t("crowflight.play.r3.title")}</h2>
        <p className={styles.summary}>
          {t("timeslip.play.summaryLine", { correct: correctCount, total: duels.length, points })}
        </p>
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.primaryButton}
            onClick={() =>
              onComplete({
                points,
                maxPoints: game.pointsPerItem * duels.length,
                greenSquares: correctCount,
                totalSquares: duels.length,
              })
            }
          >
            {t("timeslip.play.next")}
          </button>
        </div>
      </div>
    );
  }

  if (!current) return null;

  const { reference, options } = current;
  const [optA, optB] = options;
  const distA = haversineDistanceKm(reference, optA);
  const distB = haversineDistanceKm(reference, optB);

  return (
    <div>
      {!introDone && (
        <RoundIntroOverlay
          eyebrow={t("timeslip.play.roundOf", { current: roundNumber, total: 4 })}
          title={t("crowflight.play.r3.title")}
          onDone={() => setIntroDone(true)}
        />
      )}
      <h2 className={styles.roundTitle}>{t("crowflight.play.r3.title")}</h2>
      <p className={styles.duelOf}>{t("crowflight.play.r3.duelOf", { current: duelIndex + 1, total: duels.length })}</p>

      {timeLeft !== null && roundLimit !== null && (
        <CountdownBar secondsLeft={timeLeft} secondsTotal={roundLimit} />
      )}
      {timedOut && <div className={styles.timeUpBanner}>{t("timeslip.play.timeUp")}</div>}

      <DuelEngine
        optionA={{ id: optA.id, label: placeName(optA, lang) }}
        optionB={{ id: optB.id, label: placeName(optB, lang) }}
        onChoose={handleChoose}
        disabled={feedback !== null}
        prompt={t("crowflight.play.r3.prompt")}
        subject={placeName(reference, lang)}
        selectedId={feedback?.chosenId}
        selectedCorrect={feedback?.correct}
        hint={
          feedback
            ? undefined
            : {
                hintedIds,
                onHint: (id) => handleHint(id === optA.id ? optA : optB),
                getHintLabel: t("crowflight.play.hint.get"),
                viewHintLabel: t("crowflight.play.hint.view"),
              }
        }
      />
      {hintPlace && (
        <HintMapOverlay
          place={toMapPoint(hintPlace, lang)}
          lang={lang}
          onClose={() => setHintPlace(null)}
          closeLabel={t("crowflight.play.hint.close")}
        />
      )}
      {hintedIds.size > 0 && <PointsBurst points={-HINT_COST} triggerKey={`hint-${hintedIds.size}`} />}

      {feedback && (
        <>
          <PointsBurst points={feedback.correct ? game.pointsPerItem : 0} triggerKey={duelIndex} />
          <div className={`${styles.duelFeedback} ${feedback.correct ? styles.green : styles.red}`}>
            <span aria-hidden>{feedback.correct ? "✓" : "✗"}</span>{" "}
            {feedback.correct ? `+${game.pointsPerItem}` : "0"} · {feedback.text}
          </div>
          <CrowflightMap
            reference={{ id: reference.id, lat: reference.lat, lon: reference.lon, name: placeName(reference, lang) }}
            points={[
              { id: optA.id, lat: optA.lat, lon: optA.lon, name: placeName(optA, lang) },
              { id: optB.id, lat: optB.lat, lon: optB.lon, name: placeName(optB, lang) },
            ]}
            mode="arcs"
            distancesKm={{ [optA.id]: distA, [optB.id]: distB }}
            lang={lang}
          />
        </>
      )}

      <div className={styles.actions}>
        {feedback && (
          <button type="button" className={styles.primaryButton} onClick={handleNext}>
            {t("timeslip.play.next")}
          </button>
        )}
      </div>
    </div>
  );
}

function R4Round({
  game,
  difficulty,
  roundNumber,
  onComplete,
  onPointsChange,
}: {
  game: CrowflightNewGame;
  difficulty: Difficulty;
  roundNumber: number;
  onComplete: (s: RoundSummary) => void;
  onPointsChange: (points: number) => void;
}) {
  const { lang, t } = useI18n();
  const reference = game.r4.reference;
  const place = game.r4.place;
  const [guess, setGuess] = useState(1000);
  const [locked, setLocked] = useState(false);
  const roundLimit = ROUND_TIME_SECONDS[difficulty];
  const [timeLeft, setTimeLeft] = useState<number | null>(roundLimit);
  const [timedOut, setTimedOut] = useState(false);
  const [introDone, setIntroDone] = useState(false);
  const [hinted, setHinted] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const hintPenalty = hinted ? HINT_COST : 0;

  const truth = haversineDistanceKm(reference, place);

  const rawPoints = useMemo(() => (locked ? scoreLogDistanceGuess(guess, truth) : 0), [locked, guess, truth]);
  const points = Math.max(0, rawPoints - hintPenalty);

  useEffect(() => {
    onPointsChange(points);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [points]);

  const factor = guess >= truth ? guess / truth : truth / guess;
  const direction =
    factor < 1.03
      ? t("crowflight.play.r4.exact")
      : guess > truth
        ? t("crowflight.play.r4.tooFar")
        : t("crowflight.play.r4.tooClose");

  useEffect(() => {
    if (roundLimit === null || locked || !introDone) return;
    const id = window.setInterval(() => {
      setTimeLeft((prev) => {
        if (prev === null) return null;
        if (prev <= 1) {
          setTimedOut(true);
          setLocked(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => window.clearInterval(id);
  }, [locked, roundLimit, introDone]);

  useEffect(() => {
    if (!timedOut || !locked) return;
    const id = window.setTimeout(() => {
      onComplete({ points, maxPoints: 100, greenSquares: Math.round(points / 20), totalSquares: 5 });
    }, TIMEOUT_ADVANCE_DELAY_MS);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timedOut, locked, points]);

  return (
    <div>
      {!introDone && (
        <RoundIntroOverlay
          eyebrow={t("timeslip.play.roundOf", { current: roundNumber, total: 4 })}
          title={t("crowflight.play.r4.title")}
          onDone={() => setIntroDone(true)}
        />
      )}
      <h2 className={styles.roundTitle}>{t("crowflight.play.r4.title")}</h2>
      <p className={styles.targetLabel}>
        <span>{t("crowflight.play.r4.fromTo", { from: placeName(reference, lang), to: placeName(place, lang) })}</span>
        {!locked && (
          <HintButton
            revealed={hinted}
            onClick={() => {
              setHinted(true);
              setShowHint(true);
            }}
            getHintLabel={t("crowflight.play.hint.get")}
            viewHintLabel={t("crowflight.play.hint.view")}
          />
        )}
      </p>
      {showHint && (
        <HintMapOverlay
          place={toMapPoint(place, lang)}
          lang={lang}
          onClose={() => setShowHint(false)}
          closeLabel={t("crowflight.play.hint.close")}
        />
      )}
      {hinted && <PointsBurst points={-HINT_COST} triggerKey="r4-hint" />}

      {timeLeft !== null && roundLimit !== null && (
        <CountdownBar secondsLeft={timeLeft} secondsTotal={roundLimit} />
      )}
      {timedOut && <div className={styles.timeUpBanner}>{t("timeslip.play.timeUp")}</div>}

      <SliderEstimateEngine
        min={MIN_KM}
        max={MAX_KM}
        value={guess}
        onChange={setGuess}
        disabled={locked}
        ariaLabel={t("crowflight.play.r4.title")}
        lang={lang}
      />

      {locked && (
        <>
          <PointsBurst points={points} triggerKey="r4" />
          <div className={styles.revealTrack}>
            <div className={styles.revealTrackBar} />
            {/* Positions are continuous, per-round computed data (log-scale
                placement of this guess/truth pair) — not a static visual
                choice, so a real inline `left` is used here rather than a
                fixed CSS class. style-src allows 'unsafe-inline' precisely
                for cases like this, see security/headers.ts. */}
            <span className={styles.revealMarkerGuess} style={{ left: `${kmToRawPct(guess)}%` }}>
              ▲
            </span>
            <span className={styles.revealMarkerTruth} style={{ left: `${kmToRawPct(truth)}%` }}>
              ▲
            </span>
          </div>
          <p className={styles.summary}>
            {t("crowflight.play.r4.summary", {
              guess: `${formatDistanceKm(guess, lang)}`,
              truth: `${formatDistanceKm(truth, lang)}`,
              direction,
              points,
            })}
          </p>
          <CrowflightMap
            reference={{ id: reference.id, lat: reference.lat, lon: reference.lon, name: placeName(reference, lang) }}
            points={[{ id: place.id, lat: place.lat, lon: place.lon, name: placeName(place, lang) }]}
            mode="arcs"
            distancesKm={{ [place.id]: truth }}
            lang={lang}
          />
        </>
      )}

      <div className={styles.actions}>
        {!locked ? (
          <button type="button" className={styles.primaryButton} onClick={() => setLocked(true)}>
            {t("crowflight.play.r4.lockIn")}
          </button>
        ) : (
          <button
            type="button"
            className={styles.primaryButton}
            onClick={() => onComplete({ points, maxPoints: 100, greenSquares: Math.round(points / 20), totalSquares: 5 })}
          >
            {t("timeslip.play.next")}
          </button>
        )}
      </div>
    </div>
  );
}
